import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Award, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function AboutPage() {
  const { isDark } = useTheme();

  return (
    <div className="min-h-screen">
      <SEO
        {...pageSeoMeta.about}
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'About', url: '/about' },
        ])}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'About' },
      ]} />
      {/* Hero */}
      <section className="hero-gradient py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-emerald-400 text-sm font-semibold uppercase tracking-wider">Our Story</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-3 mb-6">
              About ZeroSmoke
            </h1>
            {/* Original tagline commented out
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              Your trusted destination for premium, smoke-free nicotine alternatives in India.
            </p>
            */}
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              A portfolio project showcasing full-stack web development and AI implementation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className={`py-20 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className={`text-3xl font-extrabold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Our Mission</h2>
              {/* Original mission text commented out
              <p className={`leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                We started ZeroSmoke with one simple mission — to provide adult users with a
                cleaner and more convenient alternative to traditional smoking. Our platform brings
                together some of the world's leading nicotine pouch brands such as ZYN, VELO,
                White Fox, Pablo and CLEW.
              </p>
              <p className={`leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                All our products are smoke-free, vapour-free and ash-free, designed for modern
                lifestyles where discretion and convenience matter. We carefully select every product
                to ensure authenticity, freshness, and quality.
              </p>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Giving you access to high-quality, tobacco-free products in one place — that's
                the ZeroSmoke promise.
              </p>
              */}
              <p className={`leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                We build modern, full-stack web applications tailored for businesses of all sizes.
                ZeroSmoke is a live portfolio project — a fully functional e-commerce platform
                built with React, Node.js, MySQL and integrated with AI-powered automation.
              </p>
              <p className={`leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                From product catalogs and order management to automated marketing emails and
                admin dashboards — this project demonstrates what we can build for your business,
                end to end.
              </p>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                This website does not sell any real products. All listings are for demonstration only,
                and all legalities have been followed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: <Shield className="text-blue-500" size={24} />, title: 'Full Stack', desc: 'React, Node.js, MySQL — built end to end' },
                { icon: <Award className="text-amber-500" size={24} />, title: 'AI Powered', desc: 'Smart automation and AI-driven features' },
                { icon: <Heart className="text-red-500" size={24} />, title: 'Portfolio Project', desc: 'Live demo — not a real store' },
                { icon: <Users className="text-emerald-500" size={24} />, title: 'Built for Business', desc: 'We build sites like this for you' },
              ].map((item) => (
                <div key={item.title} className={`rounded-2xl p-6 card-hover ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm mb-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    {item.icon}
                  </div>
                  <h3 className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder's Note */}
      <section className={`py-20 transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`rounded-3xl p-8 sm:p-12 shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}
          >
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6 mx-auto sm:mx-0">
              <span className="text-emerald-700 text-xl font-bold">ZS</span>
            </div>
            <h2 className={`text-2xl font-extrabold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>A Note from the Builder</h2>
            <blockquote className={`leading-relaxed space-y-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {/* Original founder note commented out
              <p>
                "ZeroSmoke was born from a simple belief — quitting smoke should not mean giving
                up choice or quality."
              </p>
              <p>
                "As someone who understands the challenges of moving away from cigarettes, I
                wanted to create a platform that makes harm-reduction alternatives accessible,
                reliable and easy to trust. Every product on ZeroSmoke is carefully selected to
                ensure authenticity, freshness and quality."
              </p>
              <p>
                "Thank you for choosing ZeroSmoke and being part of a smoke-free future."
              </p>
              */}
              <p>
                This website is a <span className={`font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>portfolio project</span> showcasing
                full-stack web development and AI implementation — built with React, Node.js, and integrated
                with intelligent automation including AI-powered features and automated marketing systems.
              </p>
              <p>
                Looking to get a website like this for your business? Whether it's an e-commerce store,
                a service platform, or a custom web app — we build it end to end with modern tech and smart automation.
              </p>
              <p className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                Reach out to us and let's build something great together.
              </p>
            </blockquote>
            <p className={`mt-6 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>— Project Planner, ZeroSmoke</p>
          </motion.div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className={`py-6 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className={`text-xs text-center leading-relaxed ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            <span className="font-medium">Disclaimer:</span> This is a portfolio website created for demonstration purposes only. ZeroSmoke does not actually sell or distribute any products. All content, product listings, and brand references are purely for illustrative purposes. All applicable legalities and intellectual property guidelines have been followed. We are a website development and technology services business — not a retailer.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">
            Ready to Explore?
          </h2>
          <p className="text-emerald-100 mb-8">
            Discover our complete range of premium nicotine pouches.
          </p>
          <Link
            to="/store"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
          >
            Visit Store <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
